import * as React from "react";
import {observer} from "mobx-react-lite";
import {
    CAlert,
    CBody1, CBody2,
    CBox,
    CButton,
    CCard,
    CCardContent,
    CCircularProgress,
    CDialog,
    CDialogActions,
    CDialogContent,
    CDialogTitle,
    CFormControl,
    CGrid,
    CGridContainer, CH1, CH4, CH6, CInputLabel, CMenuItem, CSelect, CSnackbar, CTextField
} from "../../components";
import {AccountBalanceWalletOutlined, ContentCopyOutlined, KeyOutlined} from "@mui/icons-material";
import {CIconButton} from "../../components/mui-extracted/icon-button";
import {hasValue} from "../../utils/empty-check";
import {AlertColor, IconButton, SelectChangeEvent} from "@mui/material";
import {useEffect} from "react";
import {useStores} from "../../models";
import {isEmpty} from "validate.js";
import {Action} from "../../models/action/action";

export const Actions = observer(
    function Actions() {

        const {projectStore, businessStore, actionStore} = useStores()
        const {
            projects,
        } = projectStore
        const {
            getActions,
            actions,
            isLoadingActions,
            updateAction,
            deleteAction,
            revokeActionKey
        } = actionStore

        const [selectedProject, setSelectedProject] = React.useState<string | null>((projects ? JSON.stringify(projects[0]) : null));


        const [snackBar, setSnackBar] = React.useState<{
            severity: AlertColor,
            message: string,
            isOpen: boolean
        }>({
            severity: "success",
            message: "",
            isOpen: false
        });
        const [dialog, setDialog] = React.useState<{
            isOpen: boolean,
            isAdd: boolean,
            data: {
                id?: string,
                name: string,
                amount: string,
                direction: string
            } | null
        }>({
            isOpen: false,
            isAdd: false,
            data: null
        });

        const [revokeDialog, setRevokeDialog] = React.useState<{
            isOpen: boolean,
            data: {
                id: string
            } | null
        }>({
            isOpen: false,
            data: null
        });

        const [deleteDialog, setDeleteDialog] = React.useState<{
            isOpen: boolean,
            data: {
                id: string
            } | null
        }>({
            isOpen: false,
            data: null
        });

        const [sdkDialog, setSDKDialog] = React.useState<{
            isOpen: boolean,
            platform: string,
            data: Action | null
        }>({
            isOpen: false,
            platform: "Flutter",
            data: null
        });

        const [formData, setFormData] = React.useState({
            name: "",
            amount: "",
            direction: "business_to_user"
        })
        const [isUpdating, setIsUpdating] = React.useState(false);
        const [formDataValidationMessages, setFormDataValidationMessages] = React.useState({
            name: "",
            amount: "",
            direction: ""
        })

        const showSnackBar = (message: string, severity: AlertColor) => {
            setSnackBar({
                severity: severity,
                message: message,
                isOpen: true
            });
        };

        const closeSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
            if (reason === 'clickaway') {
                return;
            }

            setSnackBar({
                severity: "success",
                message: "",
                isOpen: false
            });
        };

        const openUpdateDialog = (data) => {
            setFormData({
                name: data.name,
                amount: data.amount,
                direction: data.direction
            })
            setFormDataValidationMessages({
                name: "",
                amount: "",
                direction: ""
            })
            setDialog({
                isOpen: true,
                isAdd: false,
                data
            })
        }

        const closeUpdateDialog = () => {
            setDialog({
                isOpen: false,
                isAdd: false,
                data: null
            })
            setFormData({
                name: "",
                amount: "",
                direction: "business_to_user"
            })
            setFormDataValidationMessages({
                name: "",
                amount: "",
                direction: ""
            })
        }

        const openAddDialog = () => {
            setFormData({
                name: "",
                amount: "",
                direction: "business_to_user"
            })
            setFormDataValidationMessages({
                name: "",
                amount: "",
                direction: ""
            })
            setDialog({
                isOpen: true,
                isAdd: true,
                data: null
            })
        }

        const closeAddDialog = () => {
            setDialog({
                isOpen: false,
                isAdd: true,
                data: null
            })
            setFormData({
                name: "",
                amount: "",
                direction: "business_to_user"
            })
            setFormDataValidationMessages({
                name: "",
                amount: "",
                direction: ""
            })
        }

        const openRevokeDialog = (id: string | undefined) => {
            if (id) {
                setRevokeDialog({
                    isOpen: true,
                    data: {
                        id
                    }
                })
            }
        }

        const closeRevokeDialog = () => {
            setRevokeDialog({
                isOpen: false,
                data: null
            })
        }

        const openDeleteDialog = (id: string | undefined) => {
            if (id) {
                setDeleteDialog({
                    isOpen: true,
                    data: {
                        id
                    }
                })
            }

        }

        const closeDeleteDialog = () => {
            setDeleteDialog({
                isOpen: false,
                data: null
            })
        }

        const openSDKDialog = (project) => {
            setSDKDialog({
                isOpen: true,
                platform: "Flutter",
                data: project
            })

        }

        const closeSDKDialog = () => {
            setSDKDialog({
                isOpen: false,
                platform: "Flutter",
                data: null
            })
        }


        useEffect(() => {
            if (projects !== undefined && projects?.length > 0) {
                getActions(projects[0]?.id as string)
            }
        }, [])

        useEffect(() => {
            if (projects) {
                setSelectedProject(JSON.stringify(projects[0]))
            }
        }, [projects])

        useEffect(() => {
            if (hasValue(selectedProject) && typeof selectedProject === "string") {
                getActions(JSON.parse(selectedProject).id)
            }
        }, [selectedProject])

        const isValid = () => {

            let output = true

            const currentFormDataValidationMessages = {
                ...formDataValidationMessages
            }

            if (isEmpty(formData.name)) {
                currentFormDataValidationMessages.name = "Name is required."
                output = false
            }

            if (isEmpty(formData.amount)) {
                currentFormDataValidationMessages.amount = "Amount is required."
                output = false
            }

            if (isEmpty(formData.direction)) {
                currentFormDataValidationMessages.direction = "Direction is required."
                output = false
            }

            if (!output) {
                setFormDataValidationMessages(currentFormDataValidationMessages)
            }

            return output
        }


        const update = async () => {
            if (isValid()) {
                if (formData && dialog.data) {
                    setIsUpdating(true);
                    await updateAction(dialog.data.id, {...dialog.data, ...formData, amount: Number(formData.amount)}, JSON.parse(selectedProject as string)?.id);
                    setIsUpdating(false)
                }
                closeUpdateDialog();
            }
        }

        const revokeApiKey = async () => {
            setIsUpdating(true);
            await revokeActionKey(revokeDialog.data?.id, JSON.parse(selectedProject as string)?.id);
            setIsUpdating(false)

            closeRevokeDialog();
        }

        const submitDelete = async () => {
            setIsUpdating(true);
            await deleteAction(deleteDialog.data?.id, JSON.parse(selectedProject as string)?.id);
            setIsUpdating(false)

            closeDeleteDialog();
        }

        const add = async () => {
            if (isValid()) {
                if (formData && businessStore.business) {
                    setIsUpdating(true);
                    await actionStore.addAction({
                        ...formData,
                        amount: Number(formData.amount)
                    }, businessStore.business.id, JSON.parse(selectedProject as string).id);
                    setIsUpdating(false)
                }
                closeAddDialog();
            }
        }

        return (
            <>
                <CGrid container spacing={2}>
                    <CGrid item xs={12}>
                        <CCard>
                            <CCardContent sx={{display: "flex", flexDirection: 'row', alignItems: 'center', py: 3}}>
                                <CH4 fontWeight={'900'}>
                                    Actions
                                </CH4>
                                <CButton sx={{ml: 2}} size="medium" variant="contained" onClick={openAddDialog}>
                                    Add New
                                </CButton>

                                <CBox sx={{flex: 1}}/>
                                <CFormControl sx={{width: '25ch', ml: 2}}>
                                    <CInputLabel id="project-label">Project</CInputLabel>
                                    <CSelect
                                        labelId="project-label"
                                        id="project"
                                        value={selectedProject}
                                        label="Project"
                                        onChange={(event: SelectChangeEvent<unknown>) => {
                                            setSelectedProject(event.target.value as string);
                                        }}
                                    >
                                        {
                                            hasValue(projects) && projects?.map((project) => (
                                                <CMenuItem key={project.id}
                                                           value={JSON.stringify(project)}>{project.name}</CMenuItem>
                                            ))
                                        }
                                    </CSelect>
                                </CFormControl>
                            </CCardContent>
                        </CCard>
                    </CGrid>
                    <CGrid item xs={12}>
                        {isLoadingActions && (
                            <CBox
                                sx={{
                                    width: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    display: 'flex'
                                }}>
                                <CCircularProgress/>
                                <CBody1 sx={{ml: 2, fontWeight: 'bold', opacity: 0.6}}>
                                    Loading Actions ...
                                </CBody1>
                            </CBox>
                        )}

                        {(!isLoadingActions && !hasValue(actions)) && <CCard>
                            <CBox
                                sx={{
                                    width: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    display: 'flex'
                                }}>
                                <CBody1 sx={{ml: 2, fontWeight: 'bold', opacity: 0.6}}>
                                    No Action is found for this {JSON.parse(selectedProject as string).name}.
                                </CBody1>

                                <CButton sx={{ml: 2}} size="medium" variant="contained" onClick={openAddDialog}>
                                    Add your first action
                                </CButton>
                            </CBox></CCard>}

                        {(!isLoadingActions && hasValue(actions)) && actions?.map(action => {
                            return (
                                <CCard key={action.id} sx={{mb: 2}}>
                                    <CCardContent>
                                        <CGridContainer spacing={2}>
                                            <CGrid item xs={12}>
                                                <CH6 fontWeight={'bold'}>
                                                    {action.name}
                                                </CH6>
                                            </CGrid>
                                            <CGrid item xs={12} display={"flex"} flexDirection={"row"}
                                                   alignItems={"center"}>
                                                <AccountBalanceWalletOutlined/>
                                                <CBody1 sx={{ml: 1}} width={120}>
                                                    Amount:
                                                </CBody1>
                                                <CBody2 sx={{ml: 1, fontWeight: 700}}>
                                                    {action.amount}
                                                </CBody2>


                                            </CGrid>
                                            <CGrid item xs={12} display={"flex"} flexDirection={"row"}
                                                   alignItems={"center"}>
                                                <KeyOutlined/>
                                                <CBody1 sx={{ml: 1}} width={120}>
                                                    Direction:
                                                </CBody1>
                                                <CBody2 sx={{ml: 1, fontWeight: 700}}>
                                                    {action.direction === "user_to_business" ? "User to Business" : "Business to User"}
                                                </CBody2>

                                            </CGrid>
                                            <CGrid item xs={12} display={"flex"} flexDirection={"row"}
                                                   alignItems={"center"}>
                                                <AccountBalanceWalletOutlined/>
                                                <CBody1 sx={{ml: 1}} width={120}>
                                                    Action Key:
                                                </CBody1>
                                                <CBody2 sx={{ml: 1, backgroundColor: "#EEEEEE", p: 1, borderRadius: 2}}>
                                                    {action.actionKey}
                                                    <IconButton size={"small"} sx={{ml: 1}} aria-label="copy"
                                                                onClick={async () => {
                                                                    await navigator.clipboard.writeText(action.actionKey)
                                                                    showSnackBar("Action Key is copied successfully!", "success")
                                                                }}>
                                                        <ContentCopyOutlined fontSize={"small"}/>
                                                    </IconButton>
                                                </CBody2>


                                            </CGrid>
                                            <CGrid item xs={12} display={"flex"} justifyContent={"flex-end"}>
                                                <CButton sx={{mr: 1}} size="medium" variant="contained"
                                                         onClick={() => {
                                                             openUpdateDialog(action)
                                                         }}>
                                                    Edit
                                                </CButton>
                                                <CButton sx={{mr: 1}} size="medium" variant="contained"
                                                         onClick={() => {
                                                             openSDKDialog(action)
                                                         }}>
                                                    Usage
                                                </CButton>
                                                <CButton sx={{mr: 1}} size="medium" variant="contained"
                                                         onClick={() => {
                                                             openRevokeDialog(action.id)
                                                         }}>
                                                    Revoke Action Key
                                                </CButton>
                                                <CButton size="medium" variant="contained" color={"error"}
                                                         onClick={() => {
                                                             openDeleteDialog(action.id)
                                                         }}>
                                                    Delete
                                                </CButton>
                                            </CGrid>
                                        </CGridContainer>
                                    </CCardContent>
                                </CCard>
                            )
                        })}

                    </CGrid>
                </CGrid>

                <CSnackbar open={snackBar.isOpen} autoHideDuration={6000} onClose={closeSnackbar}>
                    <CAlert onClose={closeSnackbar} severity={snackBar.severity} sx={{width: '100%'}}>
                        {snackBar.message}
                    </CAlert>
                </CSnackbar>

                <CDialog
                    maxWidth={"xs"}
                    PaperProps={{
                        style: {
                            borderRadius: 27,
                        }
                    }}
                    componentsProps={{
                        backdrop: {
                            style: {
                                backdropFilter: "blur(3px)",
                                backgroundColor: 'rgba(0,0,30,0.4)'
                            }
                        },
                    }} open={dialog.isOpen} onClose={closeUpdateDialog} fullWidth>
                    <CDialogTitle fontWeight={"700"}
                                  sx={{mb: 2}}>{dialog.isAdd ? "Add New" : "Edit"} Action</CDialogTitle>
                    <CDialogContent>
                        <CGrid container spacing={2}>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                <CTextField
                                    fullWidth
                                    error={hasValue(formDataValidationMessages.name)}
                                    helperText={formDataValidationMessages.name}
                                    label="Action Name"
                                    variant="standard"
                                    sx={{
                                        mb: 2
                                    }}
                                    value={formData.name}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setFormData({
                                            ...formData,
                                            name: event.target.value
                                        });
                                    }}
                                />
                            </CGrid>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                <CTextField
                                    fullWidth
                                    inputMode={"numeric"}
                                    error={hasValue(formDataValidationMessages.amount)}
                                    helperText={formDataValidationMessages.amount}
                                    label="Amount"
                                    variant="standard"
                                    sx={{
                                        mb: 2
                                    }}
                                    value={formData.amount}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        if(isNaN(Number(event.target.value))) {
                                            return
                                        }
                                        setFormData({
                                            ...formData,
                                            amount: event.target.value
                                        });
                                    }}
                                />
                            </CGrid>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                <CFormControl variant="standard" fullWidth>
                                    <CInputLabel id="direction-label">Direction</CInputLabel>
                                    <CSelect
                                        labelId="direction-label"
                                        id="direction"
                                        value={formData.direction}
                                        label="Direction"
                                        onChange={(event: SelectChangeEvent<unknown>) => {
                                            setFormData({
                                                ...formData,
                                                direction: event.target.value as string
                                            });
                                        }}
                                    >
                                        <CMenuItem value={"business_to_user"}>Business to User</CMenuItem>
                                        <CMenuItem value={"user_to_business"}>User to Business</CMenuItem>
                                    </CSelect>
                                </CFormControl>
                            </CGrid>
                        </CGrid>
                    </CDialogContent>

                    {(isUpdating) ? <CDialogActions sx={{alignItems: 'center', justifyContent: 'center'}}>
                        <CCircularProgress sx={{mb: 1}}/>
                    </CDialogActions> : <CDialogActions sx={{mb: 1}}>
                        <CButton onClick={closeUpdateDialog}>Cancel</CButton>
                        <CButton size="medium" variant="contained" onClick={() => {
                            if (dialog.isAdd) {
                                add()
                            } else {
                                update()
                            }
                        }}>{dialog.isAdd ? "Add" : "Update"}</CButton>
                    </CDialogActions>}
                </CDialog>


                <CDialog
                    maxWidth={"xs"}
                    PaperProps={{
                        style: {
                            borderRadius: 27,
                        }
                    }}
                    componentsProps={{
                        backdrop: {
                            style: {
                                backdropFilter: "blur(3px)",
                                backgroundColor: 'rgba(0,0,30,0.4)'
                            }
                        },
                    }} open={revokeDialog.isOpen} onClose={closeRevokeDialog} fullWidth>
                    <CDialogTitle fontWeight={"700"} sx={{mb: 2}}>Revoke Action Key</CDialogTitle>
                    <CDialogContent>
                        <CGrid container spacing={2}>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                <CBody1>
                                    By revoking your current Action-Key you have to update your app to use the new
                                    generated Action-Key.
                                </CBody1>
                            </CGrid>
                        </CGrid>
                    </CDialogContent>

                    {(isUpdating) ? <CDialogActions sx={{alignItems: 'center', justifyContent: 'center'}}>
                        <CCircularProgress sx={{mb: 1}}/>
                    </CDialogActions> : <CDialogActions sx={{mb: 1}}>
                        <CButton onClick={closeRevokeDialog}>Cancel</CButton>
                        <CButton size="medium" variant="contained" onClick={revokeApiKey}>Revoke</CButton>
                    </CDialogActions>}
                </CDialog>

                <CDialog
                    maxWidth={"xs"}
                    PaperProps={{
                        style: {
                            borderRadius: 27,
                        }
                    }}
                    componentsProps={{
                        backdrop: {
                            style: {
                                backdropFilter: "blur(3px)",
                                backgroundColor: 'rgba(0,0,30,0.4)'
                            }
                        },
                    }} open={deleteDialog.isOpen} onClose={closeDeleteDialog} fullWidth>
                    <CDialogTitle fontWeight={"700"} sx={{mb: 2}} color={"error"}>Delete Action</CDialogTitle>
                    <CDialogContent>
                        <CGrid container spacing={2}>
                            <CGrid item xs={12} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                                <CBody1>
                                    After deleting this action from Abeld panel the installed Abeld SDK on your project
                                    won't be able to perform this action.
                                </CBody1>
                            </CGrid>
                        </CGrid>
                    </CDialogContent>

                    {(isUpdating) ? <CDialogActions sx={{alignItems: 'center', justifyContent: 'center'}}>
                        <CCircularProgress sx={{mb: 1}}/>
                    </CDialogActions> : <CDialogActions sx={{mb: 1}}>
                        <CButton onClick={closeDeleteDialog}>Cancel</CButton>
                        <CButton color={"error"} size="medium" variant="contained"
                                 onClick={submitDelete}>Delete</CButton>
                    </CDialogActions>}
                </CDialog>
                <CDialog
                    maxWidth={"sm"}
                    PaperProps={{
                        style: {
                            borderRadius: 27,
                        }
                    }}
                    componentsProps={{
                        backdrop: {
                            style: {
                                backdropFilter: "blur(3px)",
                                backgroundColor: 'rgba(0,0,30,0.4)'
                            }
                        },
                    }} open={sdkDialog.isOpen} onClose={closeSDKDialog} fullWidth>
                    <CDialogTitle fontWeight={"700"} sx={{mb: 2}}>Action Usage</CDialogTitle>
                    <CDialogContent>
                        <CGrid container spacing={2}>
                            <CGrid item xs={12}
                                   sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1}}>
                                <CFormControl fullWidth>
                                    <CInputLabel id="platform-label">Platform</CInputLabel>
                                    <CSelect
                                        labelId="platform-label"
                                        id="platform"
                                        value={sdkDialog.platform}
                                        label="Platform"
                                        onChange={(event: SelectChangeEvent<unknown>) => {
                                            setSDKDialog({...sdkDialog, platform: event.target.value as string});
                                        }}
                                    >
                                        <CMenuItem value={"Flutter"}>Flutter</CMenuItem>
                                        <CMenuItem value={"Submitted"}>React Native</CMenuItem>
                                        <CMenuItem value={"iOS"}>iOS</CMenuItem>
                                        <CMenuItem value={"Android"}>Android</CMenuItem>
                                        <CMenuItem value={"React"}>React</CMenuItem>
                                        <CMenuItem value={"Vue"}>Vue</CMenuItem>
                                        <CMenuItem value={"Angular"}>Angular</CMenuItem>
                                    </CSelect>
                                </CFormControl>
                            </CGrid>
                            <CGrid item xs={12} sx={{mt: 1}}>
                                <CBody1>New Instance:</CBody1>
                                <CBox sx={{
                                    display: "flex",
                                    mt: 1,
                                    backgroundColor: "#EEEEEE",
                                    p: 1,
                                    borderRadius: 2,
                                    alignItems: 'center'
                                }}>
                                    <CBody2 sx={{flex: 1}}>
                                        ABX abx = ABX("{JSON.parse(selectedProject as string)?.apiKey}");
                                    </CBody2>
                                    <CIconButton size={"small"} sx={{ml: 1}} aria-label="copy"
                                                 onClick={async () => {
                                                     await navigator.clipboard.writeText(`ABX abx = ABX("${JSON.parse(selectedProject as string)?.apiKey}");`)
                                                 }}>
                                        <ContentCopyOutlined fontSize={"small"}/>
                                    </CIconButton>

                                </CBox>

                            </CGrid>
                            <CGrid item xs={12} sx={{mt: 1}}>
                                <CBody1>Call Action:</CBody1>
                                <CBox sx={{
                                    display: "flex",
                                    mt: 1,
                                    backgroundColor: "#EEEEEE",
                                    p: 1,
                                    borderRadius: 2,
                                    alignItems: 'center'
                                }}>
                                    <CBody2 sx={{flex: 1}}>
                                        await abx.action("{sdkDialog.data?.actionKey}");
                                    </CBody2>
                                    <CIconButton size={"small"} sx={{ml: 1}} aria-label="copy"
                                                 onClick={async () => {
                                                     await navigator.clipboard.writeText(`await abx.action("${sdkDialog.data?.actionKey}");`)
                                                 }}>
                                        <ContentCopyOutlined fontSize={"small"}/>
                                    </CIconButton>

                                </CBox>

                            </CGrid>
                        </CGrid>
                    </CDialogContent>

                    <CDialogActions sx={{mb: 1}}>
                        <CButton onClick={closeSDKDialog}>close</CButton>
                    </CDialogActions>
                </CDialog>
            </>
        );
    }
)